import React, { useEffect, useRef, useState } from "react";
import { Send, Mic, Square } from "lucide-react";
import SuggestedActionChips from "./SuggestedActionChips";
import SaveSnapshotChip from "./SaveSnapshotChip";
import BuildPlanBadge from "./BuildPlanBadge";
import PromptSuggestionsChips from "./PromptSuggestionsChips";

// Feature-detect the Web Speech API (Chrome, Edge, Safari iOS 14.5+).
const SpeechRecognitionCtor =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

// Silence-detection window before auto-submitting a voice message.
const VOICE_AUTOSUBMIT_MS = 1500;

// Lightweight client-side intent classifier used by the "Intent Card" shown
// while dictation is active. Runs on every interim transcript — sub-ms.
// Returns { verb, subject } or null when nothing recognisable yet.
function inferIntent(transcript) {
  if (!transcript) return null;
  const t = transcript.trim().toLowerCase();
  if (t.length < 4) return null;
  const verbs = [
    { re: /\b(add|put|place|install)\b\s+(a|an|the)?\s*(.+)/i, verb: "Add" },
    { re: /\b(remove|delete|take out|get rid of)\b\s+(a|an|the)?\s*(.+)/i, verb: "Remove" },
    { re: /\b(change|switch|replace|update)\b\s+(a|an|the)?\s*(.+)/i, verb: "Change" },
    { re: /\b(make|turn)\b\s+(a|an|the)?\s*(.+)/i, verb: "Modify" },
    { re: /\b(design|create|build|draft)\b\s+(a|an|the)?\s*(.+)/i, verb: "Design" },
    { re: /\b(move|shift|reposition)\b\s+(a|an|the)?\s*(.+)/i, verb: "Relocate" },
    { re: /\b(widen|enlarge|expand|grow)\b\s+(a|an|the)?\s*(.+)/i, verb: "Enlarge" },
    { re: /\b(shrink|reduce|narrow)\b\s+(a|an|the)?\s*(.+)/i, verb: "Reduce" },
  ];
  for (const { re, verb } of verbs) {
    const m = transcript.match(re);
    if (m && m[3]) {
      // Trim trailing punctuation + over-long guesses.
      const subject = m[3].replace(/[.!?,;:]+$/, "").split(/\s+/).slice(0, 8).join(" ");
      return { verb, subject };
    }
  }
  return null;
}

export default function ChatPane({
  messages,
  onSend,
  loading,
  designSummary,
  suggestedActions = [],
  onClearSuggestions,
  buildQueue = [],
  snapshotChip,
  quickMode = false,
  onToggleQuickMode,
  quickModeFlash = false,
}) {
  const [text, setText] = useState("");
  const [listening, setListening] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [intent, setIntent] = useState(null);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const committedRef = useRef(""); // text accumulated from final results this session
  const silenceTimerRef = useRef(null);
  const pendingSubmitRef = useRef(false);
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Clean up any active recognition session on unmount.
  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop?.(); } catch (err) { console.debug("[ChatPane] mic stop (unmount) failed:", err); }
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  const submit = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || loading) return;
    onSend(t);
    setText("");
    setInterimText("");
    setIntent(null);
    // Retract the mobile keyboard on submit. Without this, iOS Safari
    // leaves the keyboard up and the input visually focused, which makes
    // the post-submit screen feel cramped. The keyboard re-appears only
    // on a manual tap of the input field (no programmatic auto-focus).
    if (typeof document !== "undefined" && document.activeElement
        && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
  };

  const scheduleAutoSubmit = () => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      // Mark an auto-submit as pending so rec.onend triggers the send after
      // the browser's speech service releases the mic. This avoids the race
      // between stop() and the final onresult.
      pendingSubmitRef.current = true;
      try { recognitionRef.current?.stop?.(); } catch (err) { console.debug("[ChatPane] mic auto-stop failed:", err); }
    }, VOICE_AUTOSUBMIT_MS);
  };

  const startListening = () => {
    if (!SpeechRecognitionCtor) return;
    const rec = new SpeechRecognitionCtor();
    rec.lang = navigator.language || "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    rec.maxAlternatives = 1;

    committedRef.current = text.trim() ? text.trim() + " " : "";
    setInterimText("");
    setIntent(null);
    pendingSubmitRef.current = false;

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          committedRef.current += r[0].transcript.trim() + " ";
        } else {
          interim += r[0].transcript;
        }
      }
      setInterimText(interim);
      const combined = (committedRef.current + interim).trimStart();
      setText(combined);
      // Live intent inference for the floating card
      setIntent(inferIntent(combined));
      // Reset the silence countdown on every new result.
      scheduleAutoSubmit();
    };
    rec.onerror = (e) => {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[SpeechRecognition] error:", e?.error || e);
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      setListening(false);
      setInterimText("");
      setIntent(null);
    };
    rec.onend = () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      setListening(false);
      setInterimText("");
      // Auto-submit after silence — fire the normal send path if we have text.
      if (pendingSubmitRef.current) {
        pendingSubmitRef.current = false;
        const final = (committedRef.current || "").trim();
        if (final && onSendRef.current) {
          onSendRef.current(final);
          setText("");
          setIntent(null);
        }
      }
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      setListening(true);
      // Prime the silence timer so staying silent after tapping still submits.
      scheduleAutoSubmit();
    } catch (err) {
      setListening(false);
    }
  };

  const stopListening = () => {
    // Manual stop — DON'T auto-submit; just stop the recogniser.
    pendingSubmitRef.current = false;
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    try { recognitionRef.current?.stop?.(); } catch (err) { console.debug("[ChatPane] manual mic stop failed:", err); }
    setListening(false);
    setInterimText("");
    setIntent(null);
  };

  const toggleMic = () => {
    if (listening) stopListening();
    else startListening();
  };

  const voiceSupported = !!SpeechRecognitionCtor;

  return (
    <div className="flex flex-col h-full border-r border-zinc-800 bg-zinc-950" data-testid="chat-pane">
      {/* Floating Intent Preview Card (top-center) while dictating */}
      {listening && (
        <div
          data-testid="voice-intent-card"
          className="fixed top-16 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100vw-2rem)] border border-orange-500/60 bg-zinc-950/95 backdrop-blur-md shadow-[0_12px_48px_rgba(0,0,0,0.7)] px-4 py-3 animate-in fade-in slide-in-from-top duration-200 pointer-events-none"
        >
          <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-orange-300 mb-1">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
            </span>
            // Voice Intent · Auto-submit in 1.5s
          </div>
          {intent ? (
            <div className="font-body text-sm text-zinc-100">
              <span className="text-orange-400 font-mono text-[10px] uppercase tracking-widest mr-1.5">
                {intent.verb}:
              </span>
              {intent.subject}
            </div>
          ) : (
            <div className="font-mono text-xs text-zinc-400 italic">
              {interimText ? `"${interimText}"` : "Listening for your command..."}
            </div>
          )}
        </div>
      )}
      {/* Title */}
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
            // Architectural Assistant
          </div>
          <div className="font-display text-base text-zinc-100 mt-0.5">
            Gemini 3 Pro
          </div>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          BIM • Live
        </div>
      </div>

      {/* Design summary strip */}
      {designSummary && (
        <div className="px-5 py-3 border-b border-zinc-800 grid grid-cols-4 gap-2">
          {[
            ["STYLE", designSummary.style],
            ["SQ FT", designSummary.square_footage?.toLocaleString?.() ?? designSummary.square_footage],
            ["BD", designSummary.bedrooms],
            ["BA", designSummary.bathrooms],
          ].map(([k, v]) => (
            <div key={k} className="border border-zinc-800 px-2 py-1.5">
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                {k}
              </div>
              <div className="font-mono text-xs text-zinc-100">{v ?? "—"}</div>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 py-5 space-y-5"
        data-testid="chat-messages"
      >
        {messages.length === 0 && !loading && (
          <div className="border border-dashed border-zinc-800 p-5 fade-up">
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-2">
              // Try
            </div>
            <ul className="space-y-2 text-sm text-zinc-300 font-body">
              <li>&ldquo;Design a 1,800 sqft modern villa with 3 bedrooms.&rdquo;</li>
              <li>&ldquo;Add a south-facing bay window to the master bedroom.&rdquo;</li>
              <li>&ldquo;Switch the wall material to exposed concrete.&rdquo;</li>
            </ul>
            {voiceSupported && (
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-4 pt-3 border-t border-zinc-800">
                // Tip — tap the mic to dictate
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => {
          // V2.0.72 — Null-guard: production streaming responses can
          // occasionally include a partial history entry (message
          // deserialized before all fields land). Rendering `m.role`
          // on undefined threw and unmounted the whole chat pane —
          // now caught by ErrorBoundary but no reason to trip it.
          if (!m || !m.role) return null;
          return (
          <div
            key={m.id || i}
            className="fade-up"
            data-testid={`chat-msg-${m.role}`}
          >
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
              {m.role === "user" ? "// You" : "// Architect"}
            </div>
            <div
              className={
                m.role === "user"
                  ? "font-body text-sm text-zinc-100 border-l-2 border-orange-500 pl-3"
                  : "font-body text-sm text-zinc-200 border-l-2 border-brass-500 pl-3 leading-relaxed"
              }
            >
              {m.content}
            </div>
          </div>
          );
        })}
        {loading && (
          <div className="fade-up" data-testid="chat-loading">
            <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 mb-1">
              // Architecting...
            </div>
            <div className="border-l-2 border-brass-500 pl-3">
              <span className="font-mono text-xs text-brass-400 architecting-dot blink-cursor">
                computing structural geometry
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <SaveSnapshotChip
        visible={!!snapshotChip?.visible}
        saving={!!snapshotChip?.saving}
        saved={!!snapshotChip?.saved}
        onSave={snapshotChip?.onSave}
        onDismiss={snapshotChip?.onDismiss}
      />
      <SuggestedActionChips
        actions={suggestedActions}
        loading={loading}
        onPick={(prompt) => {
          setText(prompt);
          onClearSuggestions?.();
        }}
        onDismiss={onClearSuggestions}
      />
      <BuildPlanBadge queue={buildQueue} isThinking={loading} />
      {/* V2.0.57 — Quick Mode chip. When ON, the parent passes
          lowDetail:true to the chat API so responses stay short and
          beat Cloudflare's 100s edge timeout. Flashes once when the
          parent auto-enables it after a 504/524. */}
      {onToggleQuickMode && (
        <div className="px-4 py-2 border-t border-zinc-800 bg-zinc-950/60 flex items-center gap-2">
          <button
            type="button"
            data-testid="chat-quick-mode-toggle"
            onClick={onToggleQuickMode}
            aria-pressed={quickMode}
            className={
              quickMode
                ? `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest border bg-brass-600 border-brass-400 text-zinc-950 transition-all ${quickModeFlash ? "ring-2 ring-brass-300 animate-pulse" : ""}`
                : "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest border border-zinc-700 text-zinc-400 hover:text-brass-300 hover:border-brass-500 transition-colors"
            }
            title={quickMode
              ? "Quick Mode is ON — responses are kept short to avoid timeouts"
              : "Quick Mode keeps responses short to avoid 100s Cloudflare timeouts"}
          >
            ⚡ Quick mode {quickMode ? "ON" : "OFF"}
          </button>
          {quickMode && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-brass-300/80">
              short responses · resubmit your prompt
            </span>
          )}
        </div>
      )}
      <form
        onSubmit={submit}
        className={`border-t bg-zinc-900/40 transition-colors ${
          listening ? "border-orange-500/60" : "border-zinc-800"
        }`}
      >
        {/* V2.0.83 — Prompt suggestions carousel (rendered ABOVE the
            interim-dictation indicator so it disappears cleanly the
            moment the user starts talking or typing). */}
        <PromptSuggestionsChips onSend={onSend} disabled={loading} />
        {/* Live-dictation interim transcript indicator */}
        {listening && (
          <div
            data-testid="chat-voice-interim"
            className="px-4 py-1.5 bg-orange-950/30 border-b border-orange-900/50 font-mono text-[10px] uppercase tracking-widest text-orange-300 flex items-center gap-2"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
            </span>
            // Listening{interimText ? ` · "${interimText}"` : "..."}
          </div>
        )}
        <div className="flex items-stretch">
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              listening
                ? "Listening — speak now..."
                : "Describe the change... e.g., add a 12ft picture window facing north"
            }
            disabled={loading}
            className="flex-1 bg-transparent px-4 py-4 font-mono text-sm text-zinc-100 placeholder:text-zinc-600 outline-none disabled:opacity-50 min-w-0"
          />
          {voiceSupported && (
            <button
              type="button"
              onClick={toggleMic}
              disabled={loading}
              data-testid="chat-voice-btn"
              aria-pressed={listening}
              aria-label={listening ? "Stop dictation" : "Dictate with voice"}
              title={listening ? "Stop dictation" : "Dictate with voice"}
              className={`relative px-5 border-l transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                listening
                  ? "border-orange-500 bg-orange-600 text-white hover:bg-orange-500"
                  : "border-zinc-800 text-brass-400 hover:bg-brass-950/40 hover:text-brass-300"
              }`}
            >
              {listening ? (
                <Square className="w-4 h-4 fill-current" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
              {listening && (
                <span className="absolute inset-1 rounded-full border-2 border-orange-300 animate-ping pointer-events-none" />
              )}
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !text.trim()}
            data-testid="chat-send-btn"
            className="px-5 border-l border-zinc-800 hover:bg-orange-600 hover:text-white text-zinc-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
